import type { GameState, HistoryEntry } from '@/types'
import { randomInt } from '@/utils/random'

export type CallResult =
  | { type: 'bomb'; bomb: number }
  | { type: 'range_update'; newMin: number; newMax: number }
  | { type: 'invalid'; reason: string }

/**
 * Pure game logic — zero DOM dependencies.
 * All mutations happen on the GameState object passed in.
 */
export class GameEngine {
  /** Create a fresh game state for a new round */
  static createState(): GameState {
    return {
      bomb: randomInt(1, 100),
      calledNumbers: new Set(),
      rangeMin: 1,
      rangeMax: 100,
      isRunning: true,
      isOver: false,
      loserNumber: null,
      history: [],
    }
  }

  /** Attempt to call a number. Returns a typed result and mutates state. */
  static callNumber(state: GameState, num: number): CallResult {
    if (!state.isRunning || state.isOver) {
      return { type: 'invalid', reason: '当前没有进行中的游戏，请开始新一局' }
    }
    if (!Number.isInteger(num) || num < state.rangeMin || num > state.rangeMax) {
      return { type: 'invalid', reason: `数字必须在 ${state.rangeMin}~${state.rangeMax} 范围内` }
    }
    if (state.calledNumbers.has(num)) {
      return { type: 'invalid', reason: `${num} 已经被报过了！` }
    }

    state.calledNumbers.add(num)

    if (num === state.bomb) {
      state.isOver = true
      state.isRunning = false
      state.loserNumber = num
      state.history.push(GameEngine.makeEntry(num, `💥 报了 ${num} — 踩雷！炸弹就是 ${state.bomb}！`, true))
      return { type: 'bomb', bomb: state.bomb }
    }

    // Narrow range
    const prevMin = state.rangeMin
    const prevMax = state.rangeMax
    if (num < state.bomb) {
      state.rangeMin = num + 1
    } else {
      state.rangeMax = num - 1
    }

    const arrow = num < state.bomb ? '➡' : '⬅'
    state.history.push(
      GameEngine.makeEntry(
        num,
        `${arrow} 报了 ${num}，范围由 ${prevMin}~${prevMax} 缩小至 ${state.rangeMin}~${state.rangeMax}`,
        false,
      ),
    )

    return { type: 'range_update', newMin: state.rangeMin, newMax: state.rangeMax }
  }

  /** Check whether a number is a valid, uncalled choice */
  static isCallable(state: GameState, num: number): boolean {
    return (
      state.isRunning &&
      !state.isOver &&
      num >= state.rangeMin &&
      num <= state.rangeMax &&
      !state.calledNumbers.has(num)
    )
  }

  private static makeEntry(number: number, message: string, isBomb: boolean): HistoryEntry {
    return { number, message, isBomb, timestamp: new Date() }
  }
}
